/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Historial de pedidos online cerrados — proto ventas-ux-v1: header ticket,
 * chips de período + estado, filas planas TicketListRow con pill de estado y
 * affordance "Tiene venta ›" cuando el pedido ya generó venta.
 */
import React, { useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  StyleSheet,
  Text,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { TicketListRow, type TicketListRowGroup } from '../../shared/ticket/TicketListRow';
import { OperationalFilterChipGroup } from '../../shared/filters/OperationalFilterPrimitives';
import {
  HISTORY_DATE_PRESET_LABELS,
  HISTORY_DATE_PRESETS,
  HISTORY_KIND_KEYS,
  HISTORY_KIND_LABELS,
  useOrdersHistoryScreen,
} from './useOrdersHistoryScreen';
import { formatCancelReasonLabel } from '../domain/cancelReasonLabels';
import {
  ORDER_STATUS_LABELS,
  formatOrderDisplayCodeLabel,
  type CustomerOrderRow,
} from '../domain/orderTypes';
import { formatMoney } from '../../shared/format/public-api';

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function statusTone(status: CustomerOrderRow['status']): 'ok' | 'bad' | 'muted' {
  if (status === 'cancelled') return 'bad';
  if (status === 'delivered' || status === 'picked_up') return 'ok';
  return 'muted';
}

function HistoryOrderRow({
  item,
  group,
  onPress,
}: {
  item: CustomerOrderRow;
  group: TicketListRowGroup;
  onPress: () => void;
}) {
  const fulfillmentLabel = item.pickup_type === 'pickup' ? 'Retiro' : 'Envío';
  const cancelLabel =
    item.status === 'cancelled' ? formatCancelReasonLabel(item.cancel_reason) : null;
  const meta = [
    `${timeLabel(item.created_at)} · ${fulfillmentLabel}`,
    cancelLabel,
    item.sale_id ? 'Tiene venta ›' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <TicketListRow
      title={item.customer_name}
      subtitle={formatOrderDisplayCodeLabel(item.display_code, item.id)}
      meta={meta}
      value={formatMoney(item.total_amount)}
      statusLabel={ORDER_STATUS_LABELS[item.status]}
      statusTone={statusTone(item.status)}
      onPress={onPress}
      group={group}
      showChevron
      accessibilityLabel={`Pedido ${formatOrderDisplayCodeLabel(item.display_code, item.id)}, ${item.customer_name}, ${ORDER_STATUS_LABELS[item.status]}`}
      testID={`orders.history.row.${item.id}`}
    />
  );
}

export const OrdersHistoryScreen = () => {
  const deliveryGate = useCapabilityGate('delivery_fulfillment', {
    title: 'Pedidos online no disponibles',
    subtitle: 'Este rubro no tiene pedidos online.',
  });
  const navigation = useNavigation<any>();
  const {
    orders,
    refreshing,
    initialLoading,
    loadingMore,
    hasMore,
    onRefresh,
    loadMore,
    kind,
    setKind,
    datePreset,
    setDatePreset,
  } = useOrdersHistoryScreen();

  const handlePressOrder = useCallback(
    (orderId: string) => {
      navigation.navigate('OnlineOrderDetail', { orderId });
    },
    [navigation],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: CustomerOrderRow; index: number }) => (
      <HistoryOrderRow
        item={item}
        group={index === orders.length - 1 ? 'groupedLast' : 'grouped'}
        onPress={() => handlePressOrder(item.id)}
      />
    ),
    [handlePressOrder, orders.length],
  );

  const keyExtractor = useCallback((item: CustomerOrderRow) => item.id, []);

  const ListHeader = useMemo(
    () => (
      <TicketModuleHeader
        kicker="Pedidos"
        title="Pedidos cerrados"
        meta="Entregados, retirados y cancelados de tu tienda online"
        tools={
          <>
            <OperationalFilterChipGroup
              options={HISTORY_DATE_PRESETS.map((preset) => ({
                value: preset,
                label: HISTORY_DATE_PRESET_LABELS[preset] ?? preset,
              }))}
              value={datePreset}
              onChange={setDatePreset}
              testIDPrefix="orders.history.date"
            />
            <OperationalFilterChipGroup
              options={HISTORY_KIND_KEYS.map((key) => ({
                value: key,
                label: HISTORY_KIND_LABELS[key],
              }))}
              value={kind}
              onChange={setKind}
              testIDPrefix="orders.history.kind"
            />
          </>
        }
      />
    ),
    [datePreset, kind, setDatePreset, setKind],
  );

  const ListFooter = useMemo(() => {
    if (!hasMore && !loadingMore) return null;
    return (
      <View style={styles.footer}>
        {loadingMore ? (
          <ActivityIndicator color={ticketColors.gold} />
        ) : (
          <TouchableOpacity onPress={() => void loadMore()} style={styles.loadMoreBtn}>
            <Text style={styles.loadMoreText}>Cargar más</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }, [hasMore, loadMore, loadingMore]);

  if (!deliveryGate.allowed) {
    return <ShellContentFrame style={styles.container}>{deliveryGate.blocked}</ShellContentFrame>;
  }

  return (
    <ShellContentFrame style={styles.container}>
      <FlatList
        data={orders}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
        ListHeaderComponent={ListHeader}
        ListFooterComponent={ListFooter}
        onEndReached={() => {
          if (hasMore) void loadMore();
        }}
        onEndReachedThreshold={0.4}
        ListEmptyComponent={
          <ModuleEmptyState
            loading={initialLoading || refreshing}
            loadingText="Cargando historial…"
            title={
              kind === 'cancelled'
                ? 'No hay cancelados en estas fechas'
                : kind === 'completed'
                  ? 'No hay pedidos entregados en estas fechas'
                  : 'No hay pedidos cerrados en estas fechas'
            }
            subtitle="Los que están en curso están en Pedidos. Las ventas también se ven en Ventas."
          />
        }
        {...VIRTUALIZED_LIST_PROPS}
      />
    </ShellContentFrame>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContent: { paddingHorizontal: 16, paddingBottom: 32, gap: 0 },
  footer: { paddingVertical: 16, alignItems: 'center' },
  loadMoreBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  loadMoreText: { color: ticketColors.ink, fontSize: 13, fontWeight: '700' },
});
